import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  MessageCircle,
  Phone,
  Star,
  Award,
  Heart,
  Sparkles
} from 'lucide-react';

const Gallery = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-soft overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-hero text-foreground">Our Gallery</h1>
            <p className="text-xl text-primary font-medium">Witness the transformations and experience our premium facilities</p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Explore our state-of-the-art facilities, advanced equipment, and see the amazing results 
              our clients have achieved through our comprehensive wellness treatments.
            </p>
          </div>
        </div>
      </section>

      {/* Facilities Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Our Premium Facilities</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Step into our elegant, modern wellness center designed for your comfort and relaxation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden group">
              <div className="relative">
                <img 
                  src="./images/hero_spa_interior_1.jpeg" 
                  alt="Serene spa interior with candles and soft lighting"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <h3 className="text-lg font-semibold">Relaxation Lounge</h3>
                  <p className="text-sm">Serene atmosphere for ultimate relaxation</p>
                </div>
              </div>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden group">
              <div className="relative">
                <img 
                  src="./images/hero_spa_interior_2.jpeg" 
                  alt="Elegant spa room with soft lighting and luxurious decor"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <h3 className="text-lg font-semibold">Treatment Room</h3>
                  <p className="text-sm">Private, comfortable treatment spaces</p>
                </div>
              </div>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden group">
              <div className="relative">
                <img 
                  src="./images/hero_spa_interior_3.jpeg" 
                  alt="Modern spa interior showcasing elegant design elements"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <h3 className="text-lg font-semibold">Reception Area</h3>
                  <p className="text-sm">Welcoming entrance and consultation space</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Treatment Equipment */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Advanced Treatment Equipment</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              State-of-the-art technology for safe, effective, and comfortable treatments
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
                <div className="relative">
                  <img 
                    src="./images/body_contouring_treatment_1.png" 
                    alt="Advanced 9-in-1 cavitation machine for body contouring"
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold">9-in-1 Cavitation System</h3>
                    <p className="text-sm">Multi-functional body contouring technology</p>
                  </div>
                </div>
              </Card>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-foreground">Professional-Grade Equipment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Our investment in cutting-edge technology ensures that every treatment delivers 
                  optimal results while maintaining the highest safety standards.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-soft">
                  <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">RF Technology</h4>
                    <p className="text-xs text-muted-foreground">Radio frequency treatments</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-soft">
                  <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Cavitation</h4>
                    <p className="text-xs text-muted-foreground">Ultrasonic fat reduction</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-soft">
                  <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center">
                    <Heart className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Vacuum Therapy</h4>
                    <p className="text-xs text-muted-foreground">Lymphatic drainage</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-soft">
                  <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Laser Therapy</h4>
                    <p className="text-xs text-muted-foreground">Precision treatments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Atmosphere */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Treatment Experience</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience the perfect blend of professional care and luxurious comfort
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="./images/body_contouring_treatment_2.jpeg" 
                alt="Professional body contouring treatment in modern wellness center"
                className="w-full h-96 object-cover rounded-2xl shadow-elegant"
              />
              <div className="absolute -bottom-6 -right-6 w-24 h-24 gradient-primary rounded-full flex items-center justify-center shadow-elegant animate-float">
                <Award className="h-8 w-8 text-white" />
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-foreground">Professional Treatment Environment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Our treatment rooms are designed to provide a comfortable, private, and hygienic 
                  environment where you can relax and enjoy your wellness journey.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Heart className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Comfortable & Private</h4>
                    <p className="text-sm text-muted-foreground">
                      Each treatment room is designed for your privacy and comfort during sessions.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Hygienic Standards</h4>
                    <p className="text-sm text-muted-foreground">
                      Strict sanitization protocols ensure a clean and safe treatment environment.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Award className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Professional Care</h4>
                    <p className="text-sm text-muted-foreground">
                      Experienced practitioners provide personalized attention throughout your treatment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Wellness Journey */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Client Wellness Journey</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See the diversity and joy of our clients as they embark on their wellness transformations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
              <div className="relative">
                <img 
                  src="./images/diverse_women_wellness_1.jpeg" 
                  alt="Diverse women celebrating wellness and natural beauty"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold">Celebrating Diversity</h3>
                  <p className="text-sm">Beauty comes in all forms</p>
                </div>
              </div>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
              <div className="relative">
                <img 
                  src="./images/diverse_women_wellness_2.jpeg" 
                  alt="Natural beauty and wellness celebration"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold">Natural Radiance</h3>
                  <p className="text-sm">Enhancing your natural glow</p>
                </div>
              </div>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
              <div className="relative">
                <img 
                  src="./images/diverse_women_wellness_3.jpeg" 
                  alt="Happy women enjoying wellness and beauty treatments"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold">Confidence & Joy</h3>
                  <p className="text-sm">Empowering transformations</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Massage & Relaxation */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Relaxation & Wellness</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience the ultimate in relaxation with our therapeutic massage and wellness treatments
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-foreground">Therapeutic Massage Experience</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Our massage therapy sessions are designed to provide deep relaxation, stress relief, 
                  and therapeutic benefits in a tranquil, professional environment.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center">
                    <Heart className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-foreground font-medium">Stress Relief & Relaxation</span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-foreground font-medium">Improved Circulation</span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center">
                    <Award className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-foreground font-medium">Professional Techniques</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="./images/wellness_massage_therapy_1.jpeg" 
                alt="Professional therapeutic massage treatment"
                className="w-full h-96 object-cover rounded-2xl shadow-elegant"
              />
              <div className="absolute -top-6 -left-6 w-24 h-24 gradient-primary rounded-full flex items-center justify-center shadow-elegant animate-float">
                <Heart className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials with Images */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Client Success Stories</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Real transformations and testimonials from our valued clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">
                  "The body contouring treatments have exceeded my expectations. The staff is incredibly 
                  professional and the results are amazing!"
                </p>
                <div className="pt-4 border-t border-border">
                  <p className="font-semibold text-foreground">Sarah M.</p>
                  <p className="text-sm text-muted-foreground">Body Contouring Client</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">
                  "I feel more energized and confident after my IV therapy sessions. The atmosphere 
                  is so relaxing and welcoming."
                </p>
                <div className="pt-4 border-t border-border">
                  <p className="font-semibold text-foreground">Thabo K.</p>
                  <p className="text-sm text-muted-foreground">IV Therapy Client</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">
                  "The massage therapy here is incredible. It's become my monthly self-care ritual. 
                  Highly recommend!"
                </p>
                <div className="pt-4 border-t border-border">
                  <p className="font-semibold text-foreground">Nomsa L.</p>
                  <p className="text-sm text-muted-foreground">Massage Therapy Client</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Ready to Experience the Glow Difference?</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Visit our beautiful facility and start your transformation journey with Polokwane's 
                premier wellness destination.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Book Your Visit
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Gallery;